import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Skeleton,
} from "@mui/material";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { Helmet } from "react-helmet";
import logoImage from "../../../assets/images/logo.png";
import callApi from "../../../services/callApi";

const ProfessionalInfo = ({ profesionalInfo, isLoading }) => {
  if (isLoading) {
    return (
      <Box sx={{ mb: 3 }}>
        <Skeleton animation="wave" variant="text" width={120} height={22} sx={{ mb: 0.5 }} />
        <Skeleton animation="wave" variant="text" width={200} height={28} sx={{ mb: 0.5 }} />
        <Skeleton animation="wave" variant="text" width={160} height={22} />
      </Box>
    );
  }

  if (!profesionalInfo) {
    return <Typography>No se encontró información del profesional.</Typography>;
  }

  const { profesional_nombres, profesional_apellidos, profesional_profesion, profesional_link_plataforma } = profesionalInfo;
  const nombreCompleto = `${profesional_nombres} ${profesional_apellidos}`;

  const handleProfessionalClick = () => {
    if (profesional_link_plataforma) {
      const profileUrl = `https://clinifica.com/pro/${profesional_link_plataforma}`;
      window.open(profileUrl, '_blank');
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography 
        sx={{ 
          fontFamily: "Quicksand-Regular", 
          color: "#191C1F",
          mb: 0,
          fontSize: { md: "16px", xs: "14px" } 
        }}
      >Creado por:</Typography>        
      <Typography 
        onClick={handleProfessionalClick}
        sx={{ 
          fontFamily: "Quicksand-Bold", 
          color: "#191C1F",
          mb: 0,
          fontSize: { md: "18px", xs: "16px" },
          cursor: profesional_link_plataforma ? "pointer" : "default",
          "&:hover": profesional_link_plataforma ? {
            color: "#3B82F6",
          } : {}
        }}
      >
        {nombreCompleto}
      </Typography>
      <Typography 
        sx={{ 
          fontFamily: "Quicksand-Regular", 
          fontSize: { md: "16px", xs: "14px" },
          mb: 0,
          color: "#191C1F"
        }}
      >
        {profesional_profesion}
      </Typography>
    </Box>
  );
};

const renderImageWithBlur = (src, alt, width, height, imageId, contentImageLoadingStates, setContentImageLoadingStates) => {
  const isImageLoadingState = contentImageLoadingStates[imageId] !== false;

  const containerStyle = {
    position: "relative",
    width: width || "100%",
    height: height || { xs: "250px", md: "400px" },
    borderRadius: 2,
    overflow: "hidden",
    backgroundColor: "#f5f5f5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  };

  const blurBackgroundStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundImage: `url(${src})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    filter: "blur(10px)",
    transform: "scale(1.1)",
    zIndex: 1
  };

  const handleImageLoad = () => {
    setContentImageLoadingStates(prev => ({
      ...prev,
      [imageId]: false
    }));
  };

  return (
    <Box sx={containerStyle}>
      {src && !isImageLoadingState && (
        <Box sx={blurBackgroundStyle} />
      )}
      
      {src && (
        <img
          src={src}
          alt={alt}
          style={{
            position: "relative",
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            zIndex: 2,
            display: isImageLoadingState ? "none" : "block"
          }}
          onLoad={handleImageLoad}
        />
      )}
      
      {(isImageLoadingState || !src) && (
        <Skeleton 
          variant="rectangular" 
          width="100%" 
          height="100%"
          animation="wave"
          sx={{ 
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 3
          }} 
        />
      )}
    </Box>
  );
};

// Función helper para formatear fecha
const formatearFechaEvento = (fecha, hora) => {
  const partes = fecha.split('-');
  const dia = parseInt(partes[0], 10);
  const mes = parseInt(partes[1], 10) - 1;
  const año = parseInt(partes[2], 10);
  
  const date = new Date(año, mes, dia);
  
  const diasSemana = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
  const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  
  const diaSemana = diasSemana[date.getDay()];
  const diaFormateado = date.getDate();
  const mesTexto = meses[date.getMonth()];
  const añoFormateado = date.getFullYear();
  
  return `${diaSemana} ${diaFormateado} de ${mesTexto} del ${añoFormateado} a las ${hora} hras`;
};

// Función helper para extraer video ID de YouTube
const extraerYouTubeVideoId = (url) => {
  if (!url || typeof url !== 'string') return null;
  
  // Agregar https si no tiene protocolo
  let urlProcesada = url.trim();
  if (!urlProcesada.match(/^https?:\/\//)) {
    urlProcesada = `https://${urlProcesada}`;
  }

  try {
    const urlObj = new URL(urlProcesada);
    const hostname = urlObj.hostname.replace('www.', '');

    // Formato youtube.com/watch?v=VIDEO_ID
    if (hostname === 'youtube.com' && urlObj.pathname === '/watch') {
      const videoId = urlObj.searchParams.get('v');
      if (videoId && videoId.match(/^[a-zA-Z0-9_-]{11}$/)) return videoId;
    }

    // Formato youtu.be/VIDEO_ID
    if (hostname === 'youtu.be') {
      const videoId = urlObj.pathname.slice(1);
      if (videoId && videoId.match(/^[a-zA-Z0-9_-]{11}$/)) return videoId;
    }
  } catch (e) {
    return null;
  }

  return null;
};

// Componente para embed de YouTube
const YouTubeEmbed = ({ videoUrl }) => {
  const videoId = extraerYouTubeVideoId(videoUrl);
  
  // Si no hay video ID válido, no renderizar nada
  if (!videoId) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          paddingBottom: "56.25%", // 16:9
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#000",
        }}
      >
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          title="Video del evento"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: "none",
          }}
        />
      </Box>
    </Box>
  );
};

const PurchaseCardSkeleton = () => (
  <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
    <CardContent>
      <Skeleton animation="wave" variant="text" width="60%" height={28} sx={{ mb: 0.5 }} />
      <Skeleton animation="wave" variant="rounded" width={90} height={24} sx={{ mb: 1, borderRadius: 2 }} />
      <Skeleton animation="wave" variant="text" width="80%" height={20} sx={{ mb: 0.25 }} />
      <Skeleton animation="wave" variant="text" width="50%" height={20} sx={{ mb: 1 }} />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
        <Skeleton animation="wave" variant="text" width={100} height={28} />
        <Skeleton animation="wave" variant="rounded" width={160} height={42} sx={{ borderRadius: 2 }} />
      </Box>
    </CardContent>
  </Card>
);

const PurchaseCard = ({ eventoInfo, onComprarClick, profesional_plan_activo }) => {
  if (!eventoInfo) return null;

  const { 
    evento_nombre, 
    evento_precio_clp,
    evento_precio_total_clp,
    evento_cupos_disponibles,
    evento_dia,
    evento_hora,
    evento_duracion_minutos,
    evento_modalidad,
    evento_direccion,
    evento_finalizado,
    evento_compra_cerrada,
    evento_agotado,
    evento_gratis
  } = eventoInfo;

  // Prioridad: plan inactivo > finalizado > compra cerrada > agotado > disponible
  const isPlanInactivo = !profesional_plan_activo;
  const isVencido = !isPlanInactivo && evento_finalizado;
  const isCompraCerrada = !isPlanInactivo && !isVencido && evento_compra_cerrada;
  const isAgotado = !isPlanInactivo && !isVencido && !isCompraCerrada && evento_agotado;
  const isDisabled = isPlanInactivo || isVencido || isCompraCerrada || isAgotado;
  const buttonText = isPlanInactivo ? "NO DISPONIBLE" : isVencido ? "EVENTO FINALIZADO" : isCompraCerrada ? "INSCRIPCIONES CERRADAS" : isAgotado ? "EVENTO AGOTADO" : evento_gratis ? "OBTENER GRATIS" : "COMPRAR ENTRADA";
  const showCuposTag = !isPlanInactivo && !isVencido && !isCompraCerrada && !isAgotado && evento_cupos_disponibles !== null && evento_cupos_disponibles !== "infinito" && evento_cupos_disponibles < 6; // Cambiado de 10 a 5
  
  const fechaFormateada = evento_dia && evento_hora ? formatearFechaEvento(evento_dia, evento_hora) : "";

  return (
    <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
      <CardContent>
        <Typography
          sx={{ 
            fontFamily: "Quicksand-Bold", 
            fontSize: { md: "18px", xs: "16px" },
            color: "#191C1F",
            mb: 0
          }}
        >
          {evento_nombre}
        </Typography>
        
        <Box
          sx={{
            display: "inline-block",
            backgroundColor: "#f0f4ff",
            color: "#3B82F6",
            fontFamily: "Quicksand-Medium",
            fontSize: { md: "15px", xs: "13px" },
            borderRadius: 2,
            paddingLeft: 0.75,
            paddingRight: 0.75,
            paddingTop: 0.25,
            paddingBottom: 0.25,
            mb: 0.5,
            marginLeft: -0.75,
            textAlign: "left",
          }}
        >
          {evento_modalidad === "presencial" ? "Evento presencial" : "Evento online"}
        </Box>

        {/* Fecha del evento */}
        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            fontSize: { md: "14px", xs: "13px" },
            color: "#626262",
            mb: 0.25
          }}
        >
          {fechaFormateada}
        </Typography>

        {/* Dirección (solo si presencial) */}
        {evento_modalidad === "presencial" && evento_direccion && (
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { md: "14px", xs: "13px" },
              color: "#626262",
              mb: 0.25
            }}
          >
            En: {evento_direccion}
          </Typography>
        )}

        {/* Duración */}
        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            fontSize: { md: "14px", xs: "13px" },
            color: "#626262",
            mb: 1
          }}
        >
          {evento_duracion_minutos}
        </Typography>

        {/* Mobile layout */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          <Typography
            sx={{ 
              fontFamily: "Quicksand-Bold", 
              color: isDisabled ? "#9e9e9e" : "#3B82F6",
              fontSize: { md: "18px", xs: "16px" },
              mb: 1
            }}
          >
            {evento_precio_total_clp}
          </Typography>

          {showCuposTag && (
            <Box
              sx={{
                fontFamily: "Quicksand-SemiBold",
                fontSize: "13px",
                color: "#E65100", // Naranjo oscuro
                textAlign: "center",
                backgroundColor: "#FFF3E0", // Naranjo suave
                padding: "4px 8px",
                borderRadius: "4px",
                mb: 1
              }}
            >
              Últimos {evento_cupos_disponibles} cupos
            </Box>
          )}

          <Button
            variant="contained"
            onClick={isDisabled ? undefined : onComprarClick}
            disabled={isDisabled}
            sx={{
              fontFamily: "Quicksand-Bold",
              backgroundColor: isDisabled ? "#bdbdbd" : "#3B82F6",
              "&:hover": { backgroundColor: isDisabled ? "#bdbdbd" : "#2563EB" },
              "&.Mui-disabled": {
                backgroundColor: "#bdbdbd",
                color: "#ffffff",
              },
              borderRadius: 2,
              width: "100%",
              py: 1.5,
              fontSize: { md: "16px", xs: "14px" },
            }}
          >
            {buttonText}
          </Button>
        </Box>

        {/* Desktop layout */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            justifyContent: "space-between",
            alignItems: "flex-end",
            mt: 1,
            gap: 2,
          }}
        >
          <Typography
            sx={{ 
              fontFamily: "Quicksand-Bold", 
              color: isDisabled ? "#9e9e9e" : "#3B82F6",
              fontSize: { md: "18px", xs: "16px" }
            }}
          >
            {evento_precio_total_clp}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              alignItems: "stretch"
            }}
          >
            {showCuposTag && (
              <Box
                sx={{
                  fontFamily: "Quicksand-SemiBold",
                  fontSize: "13px",
                  color: "#E65100", // Naranjo oscuro
                  textAlign: "center",
                  backgroundColor: "#FFF3E0", // Naranjo suave
                  padding: "4px 8px",
                  borderRadius: "4px"
                }}
              >
                Últimos {evento_cupos_disponibles} cupos
              </Box>
            )}
            <Button
              variant="contained"
              onClick={isDisabled ? undefined : onComprarClick}
              disabled={isDisabled}
              sx={{
                fontFamily: "Quicksand-Bold",
                backgroundColor: isDisabled ? "#bdbdbd" : "#3B82F6",
                "&:hover": { backgroundColor: isDisabled ? "#bdbdbd" : "#2563EB" },
                "&.Mui-disabled": {
                  backgroundColor: "#bdbdbd",
                  color: "#ffffff",
                },
                borderRadius: 2,
                fontSize: { md: "16px", xs: "14px" },
                px: 3,
                py: 1,
              }}
            >
              {buttonText}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const ErrorState = () => {
  const renderClinicaPillButton = () => {
    const handlePillClick = () => {
      window.location.href = "https://www.clinifica.com/pro";
    };

    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 4,
          mb: 4,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            mb: 1.5,
            color: "#626262",
            fontSize: { md: "16px", xs: "14px" },
          }}
        >
          ¿Eres profesional de salud independiente?
        </Typography>

        <Box
          onClick={handlePillClick}
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "white",
            borderRadius: "50px",
            px: 3,
            py: 1,
            mb: 3,
            border: "1px solid #e0e0e0",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
            "&:hover": {
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
              cursor: "pointer",
            },
            transition: "box-shadow 0.3s ease",
          }}
        >
          <Box
            sx={{
              mr: 1.5,
              color: "#4285F4",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src="/clinifica_logo.svg"
              alt="logo"
              style={{ width: "27px" }}
            />
          </Box>

          <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { md: "16px", xs: "14px" } }}>
            Crea tu perfil gratis en{" "}
            <Box
              component="span"
              sx={{ fontFamily: "Quicksand-Bold", color: "#4285F4" }}
            >
              Clinifica
            </Box>
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box
        component="img"
        src={logoImage}
        alt="Clinifica Logo"
        sx={{
          width: { xs: "150px", md: "200px" },
          height: "auto",
          display: "block",
          mx: "auto",
          p: "15px",
        }}
      />
      
      <Box
        sx={{
          maxWidth: { xs: "94%", md: "800px" },
          width: { xs: "94%", md: "90%" },
          mx: "auto",
          bgcolor: "#fbfafe",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 8,
          borderRadius: 2,
          p: { xs: 2, md: 4 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            fontSize: { md: "18px", xs: "16px" },
            color: "#9e9e9e",
            textAlign: "center",
          }}
        >
          Evento no disponible
        </Typography>
      </Box>

      {renderClinicaPillButton()}
    </>
  );
};

const DescriptionSkeleton = () => (
  <Box>
    <Skeleton animation="wave" variant="text" width="100%" height={22} sx={{ mb: 0.5 }} />
    <Skeleton animation="wave" variant="text" width="95%" height={22} sx={{ mb: 0.5 }} />
    <Skeleton animation="wave" variant="text" width="100%" height={22} sx={{ mb: 0.5 }} />
    <Skeleton animation="wave" variant="text" width="90%" height={22} sx={{ mb: 0.5 }} />
    <Skeleton animation="wave" variant="text" width="60%" height={22} />
  </Box>
);

function EventoDetalle() {
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);
  const [isLoading, setIsLoading] = useState(true);
  const [eventoInfo, setEventoInfo] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [contentImageLoadingStates, setContentImageLoadingStates] = useState({});
  
  const { evento_link } = useParams();

  const pageTitle = "Clinifica - Evento";
  const pageDescription = "Eventos especializados de salud y bienestar.";

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const url = `/agenda/eventoLeer?evento_link=${evento_link}`;
        const response = await callApi(url, "get");
        
        if (response && response.statusCode === 200 && response.evento && response.profesional) {
          setEventoInfo(response);
        } else {
          setHasError(true);
        }
      } catch (error) {
        console.error("Error al obtener la información del evento:", error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (evento_link) fetchData();
  }, [evento_link]);

  const handleComprarClick = () => {
    if (!eventoInfo?.evento || !eventoInfo?.profesional) {
      MySwal.fire(
        "Error",
        "No se pudo cargar la información del evento.",
        "error"
      );
      return;
    }

    const evento = eventoInfo.evento;

    // Usar flags de la lambda directamente
    if (evento.evento_finalizado || evento.evento_compra_cerrada || evento.evento_agotado) {
      return;
    }

    navigate("/evento/checkout", {
      state: {
        evento_nombre: evento.evento_nombre,
        evento_precio_clp: evento.evento_precio_clp,
        evento_precio_cargo_plataforma_clp: evento.evento_precio_cargo_plataforma_clp,
        evento_precio_total_clp: evento.evento_precio_total_clp,
        profesional_nombres: eventoInfo.profesional.profesional_nombres,
        profesional_apellidos: eventoInfo.profesional.profesional_apellidos,
        profesional_profesion: eventoInfo.profesional.profesional_profesion,
        eventoCompleto: evento,
        profesionalCompleto: eventoInfo.profesional
      }
    });
  };

  const processLinksInHTML = (html) => {
    if (!html) return "";
    
    return html.replace(
      /<a\s+[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi,
      (match, url, content) => {
        const processedUrl = url.match(/^https?:\/\//) ? url : `https://${url}`;
        return `<a href="${processedUrl}" target="_blank" rel="noopener noreferrer">${content}</a>`;
      }
    );
  };

  const renderEventoDescription = () => {
    if (isLoading) {
      return <DescriptionSkeleton />;
    }

    if (eventoInfo?.evento?.evento_descripcion) {
      return (
        <Box
          sx={{
            fontFamily: "Quicksand-Regular",
            fontSize: { xs: "14px", md: "16px" },
            color: "#09090a",
            lineHeight: '1.5',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            textAlign: 'left',
            '& p': {
              margin: '8px 0',
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
              '&:first-of-type': {
                marginTop: '0',
              },
              '&:last-of-type': {
                marginBottom: '0',
              }
            },
            '& ol': {
              margin: '8px 0',
              paddingLeft: '20px',
              listStyleType: 'decimal',
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
            },
            '& ul': {
              margin: '8px 0',
              paddingLeft: '20px',
              listStyleType: 'disc',
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
            },
            '& li': {
              margin: '4px 0',
              lineHeight: '1.4',
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
            },
            '& a': {
              color: '#1976D2',
              textDecoration: 'underline',
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
              '&:hover': {
                color: '#115293',
                textDecoration: 'underline',
              },
              '&:visited': {
                color: '#115293',
              }
            },
            '& strong': {
              fontWeight: 'bold',
              fontFamily: "Quicksand-Bold",
              fontSize: { xs: "14px", md: "16px" },
            },
            '& em': {
              fontStyle: 'italic',
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
            },
            '& u': {
              textDecoration: 'underline',
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
            }
          }}
          dangerouslySetInnerHTML={{ 
            __html: processLinksInHTML(eventoInfo.evento.evento_descripcion || "")
          }}
        />
      );
    }

    return (
      <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { md: "16px", xs: "14px" } }}>
        No se encontró descripción del evento.
      </Typography>
    );
  };

  const renderClinicaPillButton = () => {
    const handlePillClick = () => {
      window.location.href = "https://www.clinifica.com/pro";
    };

    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 4,
          mb: 4,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            mb: 1.5,
            color: "#626262",
            fontSize: { md: "16px", xs: "14px" },
          }}
        >
          ¿Eres profesional de salud independiente?
        </Typography>

        <Box
          onClick={handlePillClick}
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "white",
            borderRadius: "50px",
            px: 3,
            py: 1,
            mb: 3,
            border: "1px solid #e0e0e0",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
            "&:hover": {
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
              cursor: "pointer",
            },
            transition: "box-shadow 0.3s ease",
          }}
        >
          <Box
            sx={{
              mr: 1.5,
              color: "#4285F4",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src="/clinifica_logo.svg"
              alt="logo"
              style={{ width: "27px" }}
            />
          </Box>

          <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { md: "16px", xs: "14px" } }}>
            Crea tu perfil gratis en{" "}
            <Box
              component="span"
              sx={{ fontFamily: "Quicksand-Bold", color: "#4285F4" }}
            >
              Clinifica
            </Box>
          </Typography>
        </Box>
      </Box>
    );
  };

  if (hasError) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        <ErrorState />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>
      
      <Box
        component="img"
        src={logoImage}
        alt="Clinifica Logo"
        sx={{
          width: { xs: "150px", md: "200px" },
          height: "auto",
          display: "block",
          mx: "auto",
          p: "15px",
        }}
      />
      
      <Box
        sx={{
          maxWidth: { xs: "94%", md: "800px" },
          width: { xs: "94%", md: "90%" },
          mx: "auto",
          bgcolor: "#fbfafe",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 8,
          borderRadius: 2,
          p: { xs: 2, md: 4 },
        }}
      >
        <Typography 
          sx={{ 
            fontFamily: "Quicksand-Bold", 
            fontSize: { md: "28px", xs: "24px" },
            color: "#191C1F",
            mb: 0.5
          }}
        >
          {isLoading ? (
            <Skeleton animation="wave" variant="text" height={40} />
          ) : (
            eventoInfo?.evento?.evento_nombre
          )}
        </Typography>

        <Box sx={{ mb: 3 }}>
          {isLoading ? (
            <Skeleton
              animation="wave"
              variant="rectangular"
              width="100%"
              sx={{ height: { xs: 250, md: 400 }, borderRadius: 2 }}
            />
          ) : (
            eventoInfo?.evento?.evento_imagen && 
            renderImageWithBlur(
              eventoInfo.evento.evento_imagen,
              eventoInfo.evento.evento_nombre,
              "100%",
              { xs: "250px", md: "400px" },
              "main-event-image",
              contentImageLoadingStates,
              setContentImageLoadingStates
            )
          )}
        </Box>

        {isLoading ? (
          <ProfessionalInfo isLoading={true} />
        ) : (
          <ProfessionalInfo profesionalInfo={eventoInfo?.profesional} />
        )}

        {isLoading ? (
          <PurchaseCardSkeleton />
        ) : (
          <PurchaseCard 
            eventoInfo={eventoInfo?.evento} 
            onComprarClick={handleComprarClick}
            profesional_plan_activo={eventoInfo?.profesional_plan_activo}
          />
        )}

        {/* Video de YouTube (solo si existe URL válida) */}
        {!isLoading && eventoInfo?.evento?.evento_video_url && (
          <YouTubeEmbed videoUrl={eventoInfo.evento.evento_video_url} />
        )}

        <Box sx={{ mb: 3 }}>
          {renderEventoDescription()}
        </Box>

        {/* Condiciones del evento (NUEVO) */}
        {!isLoading && eventoInfo?.evento?.evento_condiciones_activas === true && eventoInfo?.evento?.evento_condiciones_descripcion && (
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontFamily: "Quicksand-Bold",
                fontSize: { md: "16px", xs: "14px" },
                color: "#191C1F",
                mb: 1,
              }}
            >
              Condiciones del evento
            </Typography>
            <Box
              sx={{
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                backgroundColor: "white",
                p: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Quicksand-Regular",
                  fontSize: { md: "16px", xs: "14px" },
                  whiteSpace: "pre-line",
                  color: "#191C1F",
                  lineHeight: 1.6,
                }}
              >
                {eventoInfo.evento.evento_condiciones_descripcion}
              </Typography>
            </Box>
          </Box>
        )}

        {isLoading ? (
          <PurchaseCardSkeleton />
        ) : (
          <PurchaseCard 
            eventoInfo={eventoInfo?.evento} 
            onComprarClick={handleComprarClick}
            profesional_plan_activo={eventoInfo?.profesional_plan_activo}
          />
        )}
      </Box>

      {renderClinicaPillButton()}
    </>
  );
}

export default EventoDetalle;
